import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { NAVBAR_THEME } from 'lib/navbar';
import BTE from 'lib/BTE';
import { setTheme as setThemeAction } from 'redux/modules/navbar';
import {
  disableVerticalScrollAction,
  enableVerticalScrollAction,
} from 'redux/modules/shared';
import { package as packagePropType } from 'lib/CustomPropTypes';

import WaffleIron from '../WaffleIron';
import WaffleList from '../WaffleList';
import WaffleHeader from '../WaffleHeader';

import styles from '../styles.module.scss';

const reduceEnabledVals = (
  enabledVals,
  [value, enabled],
) => (enabled ? [...enabledVals, value] : enabledVals);

const reduceEveryFilterVal = (
  categories,
  fn,
) => Object.entries(categories).reduce((categoryMap, [category, _values]) => {
  const values = !Array.isArray(_values) ? Object.entries(_values) : _values;
  return ({
    ...categoryMap,
    [category]: values.reduce((valueMap, entry) => ({
      ...valueMap,
      [entry.key || entry[0]]: fn(entry.key || entry[0], entry, category),
    }), {}),
  });
}, {});

const CATEGORY_TO_DISPLAY_NAME = {
  occasion: 'Occasion',
  recipient: 'Who',
  interest: 'Interests',
};

const mapActionsToProps = (dispatch) => ({
  setTheme: (theme) => dispatch(setThemeAction(theme)),
  disableVerticalScroll: () => dispatch(disableVerticalScrollAction()),
  enableVerticalScroll: () => dispatch(enableVerticalScrollAction()),
});

const mapStateToProps = ({ shared, navbar }) => ({
  vertical: shared.vertical,
  navTheme: navbar.theme,
});

@connect(mapStateToProps, mapActionsToProps)
class GiftGuideWaffle extends React.Component {
  height = null;

  static propTypes = {
    content: packagePropType.isRequired,
    vertical: PropTypes.string,
    setTheme: PropTypes.func.isRequired,
    navTheme: PropTypes.string.isRequired,
    disableVerticalScroll: PropTypes.func.isRequired,
    enableVerticalScroll: PropTypes.func.isRequired,
    pkgClassName: PropTypes.string,
  }

  static defaultProps = {
    vertical: 'today',
    pkgClassName: 'pkg waffle',
  }

  constructor(props) {
    super(props);

    const items = (props && props.content && props.content.items) || [];

    this.waffleIron = new WaffleIron(items.filter((item) => item));
    const categories = this.waffleIron.getAllCategories();

    this.state = {
      categories,
      filterValues: reduceEveryFilterVal(categories, () => false),
      isFilterActive: false,
      activeFilters: [],
      displayNames: reduceEveryFilterVal(categories, (_, { name }) => name),
    };
  }

  componentDidMount() {
    BTE.on('scroll', this.handleScroll, 'throttle');
    BTE.on('resize', this.handleResize);
    this.setHeight();
  }

  componentWillUnmount() {
    BTE.remove('scroll', this.handleScroll);
    BTE.remove('resize', this.handleResize);
  }

  setHeight() {
    const box = this.container.getBoundingClientRect();
    this.height = box.top + window.pageYOffset;
  }

  getDisplayName = (cat, key) => {
    const { displayNames } = this.state;
    return displayNames[cat] && displayNames[cat][key];
  }

  getFilteredCards = () => {
    const { filterValues } = this.state;
    const filterMap = new Map();

    Object.entries(filterValues).forEach(([category, values]) => {
      const enabledValues = Object.entries(values)
        .reduce(reduceEnabledVals, []);
      if (enabledValues.length) {
        // filter can't be empty
        filterMap.set(category, enabledValues);
      }
    });

    if (filterMap.size) {
      // if there's something filtered
      return this.waffleIron.getFilteredItems(filterMap);
    }

    // nothing filtered
    return this.waffleIron.getAllItems();
  };

  handleResize = () => {
    this.setHeight();
  }

  handleScroll = (depth) => {
    const { navTheme, setTheme } = this.props;

    // need to set height every scroll because if a user refreshes
    // in the middle of the page the height will only
    // be set once, so in that case it is forever using the LIGHT THEME.
    this.setHeight();
    if (depth > this.height && navTheme !== NAVBAR_THEME.LIGHT) {
      setTheme(NAVBAR_THEME.LIGHT);
      return;
    }

    if (depth <= this.height && navTheme !== NAVBAR_THEME.TRANSPARENT) {
      setTheme(NAVBAR_THEME.TRANSPARENT);
    }
  }

  filterOnChange = (category) => (key, value) => {
    const { filterValues } = this.state;

    const updatedFilterVals = {
      ...filterValues,
      [category]: {
        ...filterValues[category],
        [key]: value,
      },
    };

    const activeFilters = [];

    let isFilterActive = false;
    const filteredMap = new Map();
    const existingVal = new Set();
    reduceEveryFilterVal(updatedFilterVals, (filterKey, [, filtVal], filterCategory) => {
      if (filtVal) {
        if (filteredMap.get(filterCategory) !== undefined) {
          filteredMap.get(filterCategory).push(filterKey);
        } else {
          filteredMap.set(filterCategory, [filterKey]);
        }
        existingVal.add(filterKey);
        activeFilters.push({
          key: filterKey,
          category: filterCategory,
          displayName: this.getDisplayName(filterCategory, filterKey),
        });
        isFilterActive = true;
      }
    });

    this.setState({
      filterValues: updatedFilterVals,
      isFilterActive,
      activeFilters,
      categories: this.waffleIron.getUpdatedFilters(filteredMap, existingVal),
    });
  };

  clearFilter = () => {
    const { filterValues } = this.state;

    this.setState({
      filterValues: reduceEveryFilterVal(filterValues, () => false),
      isFilterActive: false,
      activeFilters: [],
      categories: this.waffleIron.getAllCategories(null),
    });
  };

  render() {
    const {
      categories,
      filterValues,
      isFilterActive,
      activeFilters,
    } = this.state;

    const {
      content,
      content: {
        id,
        metadata: {
          title,
          description,
          shortDescription,
          backgroundImageUrl,
        } = {},
        packageIndex,
      } = {},
      pkgClassName,
      disableVerticalScroll,
      enableVerticalScroll,
      vertical,
    } = this.props;

    const waffleCards = this.getFilteredCards();

    return (
      <div
        className={classNames(pkgClassName, styles.waffle)}
        ref={(e) => { this.container = e; }}
        data-packageid={id}
      >
        <WaffleHeader
          categoryToDisplay={CATEGORY_TO_DISPLAY_NAME}
          backgroundImageUrl={backgroundImageUrl}
          categories={categories}
          filterValues={filterValues}
          filterOnChange={this.filterOnChange}
          title={title}
          description={description}
          shortDescription={shortDescription}
          cardCount={waffleCards.length}
          clearFilter={this.clearFilter}
          isFilterActive={isFilterActive}
          activeFilters={activeFilters}
          disableVerticalScroll={disableVerticalScroll}
          enableVerticalScroll={enableVerticalScroll}
          packageIndex={packageIndex}
          content={content}
        />
        <WaffleList cards={waffleCards} vertical={vertical} />
      </div>
    );
  }
}

export default GiftGuideWaffle;
